import React, { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import './game.css'

const CANVAS_WIDTH = 800
const CANVAS_HEIGHT = 600

type Props = {
  onStart?: () => void
  outerRef?: React.RefObject<HTMLElement>
}

export default function Game({ onStart, outerRef }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const scoreRef = useRef<HTMLDivElement | null>(null)
  const restartBtnRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    const scoreBoard = scoreRef.current!

    let score = 0
    let gameRunning = false
    let gameStarted = false
    let restartButton: HTMLButtonElement | null = null

    // Rocket
    const rocket = {
      x: CANVAS_WIDTH / 2,
      y: CANVAS_HEIGHT - 60,
      width: 40,
      height: 60,
      speed: 6,
      color: 'red',
      hp: 50,
    }

    // Bullets
    const bullets: { x: number; y: number; size: number; speed: number; color: string }[] = []
    const bulletSpeed = 8
    const bulletSize = 6

    // Asteroids
    const asteroids: { x: number; y: number; size: number; speed: number; rotation: number; rotationSpeed: number }[] = []
    const asteroidSize = 25
    const rotationSpeed = 0.02

    // Stars
    const numStars = 80
    const stars: { x: number; y: number; radius: number; speed: number }[] = []

    for (let i = 0; i < numStars; i++) {
      stars.push({
        x: Math.random() * CANVAS_WIDTH,
        y: Math.random() * CANVAS_HEIGHT,
        radius: Math.random() * 2 + 1,
        speed: Math.random() * 0.8 + 0.3,
      })
    }

    function drawStars() {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)'
      ctx.shadowBlur = 5
      ctx.shadowColor = '#2c0c00'
      stars.forEach((star) => {
        star.y += star.speed
        if (star.y > CANVAS_HEIGHT) {
          star.y = 0
          star.x = Math.random() * CANVAS_WIDTH
        }
        ctx.beginPath()
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2)
        ctx.fill()
      })
    }

    // Mouse/touch/pointer tracking
    let mouseX = rocket.x
    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      mouseX = (e.clientX - rect.left) * (canvas.width / rect.width)
    }
    const onPointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      mouseX = (e.clientX - rect.left) * (canvas.width / rect.width)
    }
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches && e.touches.length > 0) {
        const rect = canvas.getBoundingClientRect()
        const t = e.touches[0]
        mouseX = (t.clientX - rect.left) * (canvas.width / rect.width)
      }
      e.preventDefault()
    }
    canvas.addEventListener('mousemove', onMouseMove)
    canvas.addEventListener('pointermove', onPointerMove)
    canvas.addEventListener('touchmove', onTouchMove, { passive: false })

    // Input
    const shootBullet = () => {
      const bullet = {
        x: rocket.x,
        y: rocket.y - rocket.height / 2,
        size: bulletSize,
        speed: bulletSpeed,
        color: '#ff4619',
      }
      bullets.push(bullet)
      canvas.style.boxShadow = '0 0 30px rgb(255, 50, 50)'
      setTimeout(() => {
        canvas.style.boxShadow = '0 0 20px rgb(124, 4, 4)'
      }, 50)
    }

    const onClick = () => shootBullet()
    const onTouchStart = (e: TouchEvent) => {
      shootBullet()
      e.preventDefault()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        shootBullet()
        e.preventDefault()
      }
    }
    canvas.addEventListener('click', onClick)
    canvas.addEventListener('touchstart', onTouchStart, { passive: false })
    window.addEventListener('keydown', onKeyDown)

    function spawnAsteroid() {
      const currentSpeed = 1.5 + score * 0.05
      const asteroid = {
        x: asteroidSize + Math.random() * (CANVAS_WIDTH - asteroidSize * 2),
        y: -asteroidSize,
        size: asteroidSize,
        speed: currentSpeed,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: Math.random() * 0.05 - 0.025,
      }
      asteroids.push(asteroid)
    }

    function checkCollision(x1: number, y1: number, r1: number, x2: number, y2: number, r2: number) {
      const dx = x2 - x1
      const dy = y2 - y1
      const distance = Math.sqrt(dx * dx + dy * dy)
      return distance < r1 + r2
    }

    function createBigExplosion(x: number, y: number) {
      const particleCount = 200
      for (let i = 0; i < particleCount; i++) {
        const angle = (i / particleCount) * Math.PI * 2
        const speed = 4 + Math.random() * 3
        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: 2 + Math.random() * 3,
          color: `hsl(${Math.random() * 360}, 100%, 50%)`,
          life: 40 + Math.random() * 30,
        })
      }
    }

    function checkRocketCollision() {
      for (let i = asteroids.length - 1; i >= 0; i--) {
        const asteroid = asteroids[i]
        if (checkCollision(rocket.x, rocket.y, rocket.width / 2, asteroid.x, asteroid.y, asteroid.size * 0.7)) {
          asteroids.splice(i, 1)
          createExplosion(asteroid.x, asteroid.y)
          rocket.hp -= 10
          if (rocket.hp <= 0) {
            const lastX = rocket.x
            const lastY = rocket.y
            createBigExplosion(lastX, lastY)
            rocket.width = 0
            rocket.height = 0
            gameRunning = false
            setTimeout(() => {
              toast.error('Game Over! The rocket has been destroyed.', {
                description: 'Press Play Again to retry',
              })
            }, 300)
          }
          break
        }
      }
    }

    // Particles
    const particles: { x: number; y: number; vx: number; vy: number; size: number; color: string; life: number }[] = []

    function createExplosion(x: number, y: number) {
      const particleCount = 100
      for (let i = 0; i < particleCount; i++) {
        const angle = (i / particleCount) * Math.PI * 2
        const speed = 3 + Math.random() * 2
        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: 1.6 + Math.random() * 2,
          color: `hsl(${Math.random() * 360}, 100%, 50%)`,
          life: 25 + Math.random() * 20,
        })
      }
    }

    function updateParticles() {
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        p.x += p.vx
        p.y += p.vy
        p.life--
        if (p.life <= 0) {
          particles.splice(i, 1)
          continue
        }
        ctx.globalAlpha = p.life / 40
        ctx.fillStyle = p.color
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 1
      }
    }

    function drawRocket() {
      ctx.save()
      // Body
      ctx.fillStyle = rocket.color
      ctx.beginPath()
      ctx.moveTo(rocket.x, rocket.y - rocket.height / 2)
      ctx.lineTo(rocket.x + rocket.width / 2, rocket.y + rocket.height / 2)
      ctx.lineTo(rocket.x - rocket.width / 2, rocket.y + rocket.height / 2)
      ctx.closePath()
      ctx.fill()
      // Window
      ctx.fillStyle = '#065ca2'
      ctx.beginPath()
      ctx.arc(rocket.x, rocket.y - 5, 8, 0, Math.PI * 2)
      ctx.fill()
      // Flames
      const flameHeight = 15 + Math.random() * 5
      ctx.fillStyle = 'orange'
      ctx.beginPath()
      ctx.moveTo(rocket.x - rocket.width / 3, rocket.y + rocket.height / 2)
      ctx.lineTo(rocket.x, rocket.y + rocket.height / 2 + flameHeight)
      ctx.lineTo(rocket.x + rocket.width / 3, rocket.y + rocket.height / 2)
      ctx.closePath()
      ctx.fill()
      ctx.fillStyle = 'yellow'
      ctx.beginPath()
      ctx.moveTo(rocket.x - rocket.width / 6, rocket.y + rocket.height / 2)
      ctx.lineTo(rocket.x, rocket.y + rocket.height / 2 + flameHeight * 0.7)
      ctx.lineTo(rocket.x + rocket.width / 6, rocket.y + rocket.height / 2)
      ctx.closePath()
      ctx.fill()
      ctx.restore()
    }

    function updateAsteroids() {
      for (let i = asteroids.length - 1; i >= 0; i--) {
        const asteroid = asteroids[i]
        asteroid.y += asteroid.speed
        asteroid.rotation += rotationSpeed
        if (asteroid.y > CANVAS_HEIGHT + asteroid.size) {
          asteroids.splice(i, 1)
          continue
        }
        ctx.save()
        ctx.translate(asteroid.x, asteroid.y)
        ctx.rotate(asteroid.rotation)
        ctx.fillStyle = '#5f1b00'
        ctx.beginPath()
        for (let j = 0; j < 11; j++) {
          const angle = (j / 11) * Math.PI * 2
          const x = Math.cos(angle) * asteroid.size
          const y = Math.sin(angle) * asteroid.size
          if (j === 0) ctx.moveTo(x, y)
          else ctx.lineTo(x, y)
        }
        ctx.closePath()
        ctx.fill()
        ctx.strokeStyle = '#011627'
        ctx.lineWidth = 3
        ctx.stroke()
        ctx.restore()
      }
    }

    function updateBullets() {
      for (let i = bullets.length - 1; i >= 0; i--) {
        const bullet = bullets[i]
        bullet.y -= bullet.speed
        let hitAsteroid = false
        for (let j = asteroids.length - 1; j >= 0; j--) {
          const asteroid = asteroids[j]
          if (checkCollision(bullet.x, bullet.y, bullet.size, asteroid.x, asteroid.y, asteroid.size * 0.7)) {
            asteroids.splice(j, 1)
            score += 1
            createExplosion(asteroid.x, asteroid.y)
            hitAsteroid = true
            break
          }
        }
        if (bullet.y < -bullet.size || hitAsteroid) {
          bullets.splice(i, 1)
          continue
        }
        ctx.fillStyle = bullet.color
        ctx.beginPath()
        ctx.arc(bullet.x, bullet.y, bullet.size, 0, Math.PI * 2)
        ctx.fill()
        ctx.shadowBlur = 18
        ctx.shadowColor = 'red'
        ctx.fill()
        ctx.shadowBlur = 0
      }
    }

    // Buttons
    function createButton(text: string) {
      const button = document.createElement('button')
      button.className = 'game-button'
      button.textContent = text
      return button
    }

    function showStartButton() {
      // ensure only one start button exists
      const container = canvas.parentElement as HTMLElement
      const existing = container?.querySelector('button.game-button[data-kind="start"]') as HTMLButtonElement | null
      if (existing) existing.remove()

      const startButton = createButton('Ready')
      startButton.setAttribute('data-kind', 'start')
      startButton.onclick = () => {
        gameStarted = true
        gameRunning = true
        startButton.remove()
        // notify parent and lock scroll
        onStart?.()
        gameLoop()
      }
      container.style.position = 'relative'
      container.appendChild(startButton)
    }

    function showRestartButton() {
      const container = canvas.parentElement as HTMLElement
      const existing = container?.querySelector('button.game-button[data-kind="restart"]') as HTMLButtonElement | null
      if (existing) {
        restartButton = existing
        return
      }
      if (restartButton) return
      restartButton = createButton('Play Again')
      restartButton.setAttribute('data-kind', 'restart')
      restartButton.onclick = () => {
        score = 0
        gameRunning = true
        gameStarted = true
        rocket.hp = 50
        rocket.width = 40
        rocket.height = 60
        rocket.x = CANVAS_WIDTH / 2
        asteroids.length = 0
        bullets.length = 0
        particles.length = 0
        restartButton?.remove()
        restartButton = null
        gameLoop()
      }
      container.appendChild(restartButton)
      restartBtnRef.current = restartButton
    }

    // Main loop
    const gameLoop = () => {
      if (!gameStarted) return
      if (!gameRunning) {
        showRestartButton()
        return
      }
      ctx.fillStyle = 'black'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      drawStars()

      if (mouseX < rocket.x - 2) rocket.x -= rocket.speed
      else if (mouseX > rocket.x + 2) rocket.x += rocket.speed
      rocket.x = Math.max(rocket.width / 2, Math.min(canvas.width - rocket.width / 2, rocket.x))

      const currentSpawnRate = 0.01 + score * 0.001
      if (Math.random() < currentSpawnRate) spawnAsteroid()

      checkRocketCollision()
      updateAsteroids()
      updateBullets()
      updateParticles()
      drawRocket()

      scoreBoard.textContent = `Score: ${score} | HP: ${rocket.hp}`
      requestAnimationFrame(gameLoop)
    }

    // Init
    canvas.width = CANVAS_WIDTH
    canvas.height = CANVAS_HEIGHT
    rocket.x = CANVAS_WIDTH / 2
    rocket.y = CANVAS_HEIGHT - 60
    showStartButton()

    // Cleanup
    return () => {
      canvas.removeEventListener('mousemove', onMouseMove)
      canvas.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('touchmove', onTouchMove as any)
      canvas.removeEventListener('click', onClick)
      canvas.removeEventListener('touchstart', onTouchStart as any)
      window.removeEventListener('keydown', onKeyDown)
      restartBtnRef.current?.remove()
    }
  }, [])

  return (
    <section id="game" className="py-12" ref={outerRef as any}>
      <div className="container mx-auto text-center">
        <h2 className="text-3xl mb-2 text-red-500">Space Shooter </h2>
        <p className="text-rose-200/80 mb-4 font-extralight">Try out this quick mini-game, click on "Ready"</p>
        <div className="flex flex-col items-center">
          <canvas id="game-canvas" ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} />
          <div id="score-board" ref={scoreRef}>Score: 0</div>
        </div>
      </div>
    </section>
  )
}
